import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import moment from "moment";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { FormatFunction } from "PFCore/helpers/date_formatter";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useActivityDelete } from "PFCore/hooks/queries/activity/use_activity_delete";
import { createActivity } from "PFCore/services/activities";
import { Activity, Profile } from "PFCore/types";
import { ActivityCustomField } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { sanitizeCustomFieldsForAPI } from "./edit_internal_experience_form.utils";

type ExperienceFormValues = {
  positionId?: string;
  startDate: string | null;
  endDate: string | null;
  current: boolean;
  location: string;
  engagementHours?: number;
  description: string;
  name: string;
  customFields: ActivityCustomField[];
};

type UseExperienceFormSubmissionParams = {
  experience?: Activity;
  isNew: boolean;
  profile: Profile;
  formatDatabaseDateTime: FormatFunction;
  handleSuccess: (experience: Activity) => void;
  handleRemove: () => void;
  values: ExperienceFormValues;
};

export const useExperienceFormSubmission = ({
  experience,
  isNew,
  profile,
  formatDatabaseDateTime,
  handleSuccess,
  handleRemove,
  values
}: UseExperienceFormSubmissionParams) => {
  const [errors, setErrors] = useState({});
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "common.experience" });
  const { t: tBase } = useTranslation();
  const { update: updateActivity } = useActivityUpdate(experience?.id);
  const { mutateAsync: deleteActivity } = useActivityDelete();
  const workTemplate = useTemplate(TemplateKey.Work);

  const {
    positionId,
    startDate,
    endDate,
    current,
    location,
    engagementHours,
    description,
    name,
    customFields
  } = values;

  const formData = () => ({
    metadata: {
      engagementHours,
      positionId,
      dateRange: {
        startDate: !current && !endDate ? null : formatDatabaseDateTime(startDate),
        endDate: endDate ? formatDatabaseDateTime(endDate) : null,
        current
      },
      location
    },
    customFields: sanitizeCustomFieldsForAPI(customFields),
    name,
    description
  });

  const isChanged = () => {
    const data = pick(experience, ["custom_fields", "name", "description"]);
    data.metadata = pick(experience?.metadata, ["engagement_hours", "position_id", "date_range", "location"]);
    if (data.metadata.date_range) {
      data.metadata.date_range.current = data.metadata.date_range.current || false;
    }
    return !isEqual(data, formData());
  };

  const handleSuccessCallback = (updatedExperience) => {
    showGrowl({
      message: isNew ? t("internalExperience.created") : t("internalExperience.updated")
    });
    handleSuccess &&
      handleSuccess({
        ...updatedExperience,
        statistics: {
          ...profile?.statistics
        }
      });
  };

  const handleError = (newErrors) => {
    setErrors(newErrors);

    if (newErrors.base) {
      showGrowl({
        message: newErrors.base,
        kind: "error"
      });
    }
  };

  const handleRemoveCallback = () => {
    if (!isNew) {
      if (window.confirm(tBase("sureToDelete", { key: name }))) {
        deleteActivity({ activityId: experience!.id }).then(() => handleRemove && handleRemove());
      }
    } else {
      if (isChanged()) {
        if (window.confirm(t("confirmCloseDialog"))) {
          handleRemove && handleRemove();
        }
      } else {
        handleRemove && handleRemove();
      }
    }
  };

  const handleSubmit = () => {
    setErrors({});
    const payload = {
      templateId: workTemplate?.id,
      postUntil: moment().add(workTemplate?.default_post_until_days, "days").format(),
      ...formData()
    };

    const apiFunction = isNew ? createActivity : updateActivity;
    return apiFunction(payload);
  };

  return {
    errors,
    isDirty: isChanged(),
    handleSubmit,
    handleRemoveCallback,
    handleSuccessCallback,
    handleError
  };
};
